import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { createClient } from "@supabase/supabase-js";
import Footer from "./Footer";
import { Barcode } from "lucide-react";
import toast from "react-hot-toast";
import Topbar from "./Topbar";

const apiBase = process.env.REACT_APP_API_URL;
const supabase = createClient(process.env.REACT_APP_SUPABASE_URL, process.env.REACT_APP_SUPABASE_KEY, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
  },
});

const regions = [
  {
    code: "LCK",
    name: "LCK",
    color: "28, 25, 44",
    color2: "73, 78, 205",
    banner: "/banners/LCK-banner.png",
    trophyLocked: "/banners/lck-trophy.png",
    trophyUnlocked: "/banners/lck-trophy-clear.png",
    maxPlayers: 12,
  },
  {
    code: "LEC",
    name: "LEC",
    color: "107, 104, 255",
    color2: "0, 229, 190",
    banner: "/banners/LEC-banner.png",
    trophyLocked: "/banners/lec-trophy.png",
    trophyUnlocked: "/banners/lec-trophy-clear.png",
    maxPlayers: 12,
  },
  {
    code: "LPL",
    name: "LPL",
    color: "0, 0, 0",
    color2: "252, 12, 73",
    banner: "/banners/LPL-banner.png",
    trophyLocked: "/banners/lpl-trophy.png",
    trophyUnlocked: "banners/lpl-trophy-clear.png",
    maxPlayers: 12,
  },
  {
    code: "LCP",
    name: "LCP",
    color: "247, 137, 74",
    color2: "0, 0, 0",
    banner: "/banners/LCP-banner.png",
    trophyLocked: "/banners/lcp-trophy.png",
    trophyUnlocked: "banners/lcp-trophy-clear.png",
    maxPlayers: 12,
  },
];

const internationalEvents = [
  {
    code: "FIRST",
    name: "First Stand Tournament",
    color: "255, 86, 1",
    color2: "255, 86, 1",
    startDate: "2027-03-06",
    endDate: "2027-03-22",
    banner: "/banners/first-stand.png",
    trophyLocked: "/banners/first-trophy.png",
    trophyUnlocked: "/banners/first-trophy-clear.png",
    maxPlayers: 12,
  },
  {
    code: "MSI",
    name: "MSI",
    color: "101, 40, 100",
    color2: "254, 27, 36",
    startDate: "2026-05-15",
    endDate: "2026-06-15",
    banner: "/banners/msi-banner.svg",
    trophyLocked: "/banners/msi-trophy.png",
    trophyUnlocked: "/banners/msi-trophy-clear.png",
    maxPlayers: 12,
  },
  {
    code: "WORLDS",
    name: "World Championship",
    color: "0, 0, 0",
    color2: "21, 97, 255",
    startDate: "2026-10-01",
    endDate: "2026-11-10",
    banner: "/banners/worlds-banner.png",
    trophyLocked: "/banners/worlds-trophy.png",
    trophyUnlocked: "/banners/worlds-trophy-clear.png",
    maxPlayers: 8,
  },
];

export default function RegionSelectPage() {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState(0);
  const [showCreate, setShowCreate] = useState(false);
  const [showJoin, setShowJoin] = useState(false);
  const [selectedRegion, setSelectedRegion] = useState("");
  const [inviteCode, setInviteCode] = useState("");
  const [customName, setCustomName] = useState("");
  const [createdInviteCode, setCreatedInviteCode] = useState(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [activeLeagues, setActiveLeagues] = useState([]);
  const [trophies, setTrophies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState("");
  const [seasonStatus, setSeasonStatus] = useState({});

  useEffect(() => {
    const init = async () => {
      try {
        const {
          data: { session },
          error: sessionError,
        } = await supabase.auth.getSession();

        if (sessionError || !session?.user) {
          console.error("User not logged in or session expired:", sessionError?.message);
          await supabase.auth.signOut();
          window.location.href = "/login";
          return;
        }

        const accessToken = session.access_token;
        setToken(accessToken);

        // Fetch all required data in parallel
        const [ticketsRes, leaguesRes, trophiesRes, seasonRes] = await Promise.all([
          axios.get(`${apiBase}/api/international/tickets`, {
            headers: { Authorization: `Bearer ${accessToken}` },
          }),
          axios.get(`${apiBase}/api/leagues/active`, {
            headers: { Authorization: `Bearer ${accessToken}` },
          }),
          axios.get(`${apiBase}/api/user/trophies`, {
            headers: { Authorization: `Bearer ${accessToken}` },
          }),
          axios.get(`${apiBase}/api/seasons/status`, { headers: { Authorization: `Bearer ${accessToken}` } }),
        ]);

        const mapped = {};
        seasonRes.data.forEach((s) => {
          const baseRegion = s.region.replace(/_W_\d+$/, "");
          if (!mapped[baseRegion] || s.state !== "ended") {
            mapped[baseRegion] = s;
          }
        });

        // Update state
        setTickets(ticketsRes.data.tickets);
        setActiveLeagues(leaguesRes.data);
        setTrophies(trophiesRes.data);
        setSeasonStatus(mapped);
      } catch (err) {
        console.error("Error initializing region page:", err);
      } finally {
        setLoading(false);
      }
    };

    init();
  }, []);

  const handleJoinRegion = async (regionCode, maxPlayers) => {
    try {
      const res = await axios.post(
        `${apiBase}/api/league/join/${regionCode}`,
        {
          maxPlayers: Number(maxPlayers),
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (res.data.success) {
        navigate(`/dashboard/league/${res.data.league_id}/build`);
      } else {
        toast.error(res.data.message || "Failed to sell player");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Something went wrong while joining the league.");
    }
  };

  const handleJoinEvent = async (eventCode, maxPlayers) => {
    try {
      if (!eventCode) {
        toast.error("Invalid event.");
        return;
      }

      // Redeem Ticket
      const redeemRes = await axios.post(
        `${apiBase}/api/international/redeem`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (!redeemRes.data?.success) {
        toast.error(redeemRes.data?.message || "Failed to redeem ticket.");
        return;
      }

      // 2️⃣ Join League Using Existing League System
      const joinRes = await axios.post(
        `${apiBase}/api/league/join/${eventCode}`,
        {
          maxPlayers: Number(maxPlayers),
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (joinRes.data.success) {
        navigate(`/dashboard/league/${joinRes.data.league_id}/build`);
      } else {
        toast.error(joinRes.data.message || "Failed to join event.");
      }
    } catch (err) {
      console.error(`Error joining ${eventCode}:`, err);
      toast.error(err.response?.data?.message || "Something went wrong while joining the event.");
    }
  };

  const handleCreateFriendLeague = async () => {
    if (!selectedRegion) return alert("Select a region first!");
    const selectedRegionData = regions.find((r) => r.code === selectedRegion);
    const maxPlayers = selectedRegionData?.maxPlayers ?? null;
    try {
      const res = await axios.post(
        `${apiBase}/api/league/create`,
        { region: selectedRegion, is_private: true, custom_name: customName, maxPlayers: maxPlayers || null },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const { invite_code } = res.data;
      setCreatedInviteCode(invite_code);
      setShowCreate(false);
      setShowInviteModal(true);
    } catch (err) {
      console.error("Error creating friends league:", err);
      alert("Failed to create league.");
    }
  };

  const handleJoinByCode = async () => {
    if (!inviteCode.trim()) return alert("Enter a valid invite code!");
    try {
      const res = await axios.post(
        `${apiBase}/api/league/join-by-code`,
        { invite_code: inviteCode },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setShowJoin(false);
      navigate(`/dashboard/league/${res.data.league_id}/build`);
    } catch (err) {
      console.error("Error joining by code:", err);
      alert(err.response?.data?.error || "Invalid code");
    }
  };

  if (loading) return <p style={{ color: "white", textAlign: "center" }}>Loading...</p>;

  const now = new Date();

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        flexDirection: "column",
      }}>
      <Topbar showBudget={false} showLinks={false} showEmptyRewards={true} />
      <div className="region-page">
        <img src="/logo.svg" alt="logo" style={{ width: "200px", marginBottom: "10px" }} />
        <p style={{ color: "white", marginBottom: "40px", textAlign: "center", lineHeight: "30px" }}>
          Build your dream fantasy team and compete for glory — choose your region or international event to begin.<br></br>
          <b style={{ opacity: "0.6", fontWeight: "500" }}>Claim International Tickets by playing and qualifying from your regional leagues.</b>
        </p>

        <div
          style={{
            display: "flex",
            width: "100%",
            justifyContent: "space-between",
            maxWidth: "1400px",
            margin: "0 0 10px 0",
            alignItems: "center",
          }}>
          <h1 className="region-title" style={{ color: "white" }}>
            Choose a league
          </h1>
          <div style={{ display: "flex", alignItems: "center", gap: "4px", borderRadius: "6px", background: "#3273fa", padding: "2px 12px" }}>
            <img src="/international-ticket.png" style={{ height: "32px" }} alt="International Ticket" />
            <h4 style={{ color: "white" }}>{tickets}</h4>
          </div>
        </div>
        {/* Create League Modal */}
        {showCreate && (
          <div className="modal-overlay" onClick={() => setShowCreate(false)}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
              <h2>Create a Friends League</h2>
              <p>Choose a region where you and your friends will compete.</p>
              <p style={{ fontSize: "14px", opacity: "0.6" }}>Private leagues do not reward trophies or tokens, only international tickets</p>
              <select value={selectedRegion} onChange={(e) => setSelectedRegion(e.target.value)} style={{ padding: 10 }}>
                <option value="">Select Region</option>
                {regions.map((r) => (
                  <option key={r.code} value={r.code}>
                    {r.name}
                  </option>
                ))}
              </select>
              {selectedRegion && (
                <p style={{ fontSize: "13px", opacity: 0.7 }}>Max players: {regions.find((r) => r.code === selectedRegion)?.maxPlayers}</p>
              )}

              <input
                type="text"
                value={customName}
                onChange={(e) => setCustomName(e.target.value)}
                placeholder="Enter custom league name"
                style={{
                  padding: "10px",
                  borderRadius: "6px",
                  border: "1px solid #555",
                  backgroundColor: "#222",
                  color: "white",
                  width: "100%",
                  marginBottom: "12px",
                }}
              />
              <button onClick={handleCreateFriendLeague} disabled={!customName.trim()} className="edit-btn">
                Create League
              </button>
            </div>
          </div>
        )}

        {/* Join by Code Modal */}
        {showJoin && (
          <div className="modal-overlay" onClick={() => setShowJoin(false)}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
              <h2>Join Friends League</h2>
              <p>Enter your invite code below:</p>
              <input type="text" value={inviteCode} onChange={(e) => setInviteCode(e.target.value.toUpperCase())} placeholder="e.g. XH7ZQF" />
              <button onClick={handleJoinByCode} className="edit-btn" style={{ marginTop: "20px" }}>
                Join
              </button>
            </div>
          </div>
        )}

        {showInviteModal && (
          <div className="modal-overlay" onClick={() => setShowInviteModal(false)}>
            <div
              className="modal"
              onClick={(e) => e.stopPropagation()}
              style={{ display: "flex", flexDirection: "column", alignItems: "center", position: "relative" }}>
              <img src="/success_go.svg" alt="Success Fingers" style={{ position: "absolute", top: "-70px", height: " 110px" }} />
              <h2 style={{ marginTop: "40px", marginBottom: " 0" }}>League Created!</h2>
              <p>Your Friends League is ready. Share this invite code:</p>

              <div className="invite-box">
                <strong style={{ fontSize: "24px", textAlign: "center", color: "rgb(240, 217, 125)" }}>{createdInviteCode}</strong>
              </div>
              <div style={{ gap: 20, display: "flex", alignItems: "center", marginTop: "30px" }}>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(createdInviteCode);
                    alert("Invite code copied!");
                  }}
                  style={{
                    padding: "8px 20px",
                    cursor: "pointer",
                    background: "#3273FA",
                    border: "1px solid rgba(255, 255, 255, 0.14)",
                    color: "white",
                    borderRadius: "90px",
                    fontSize: "16px",
                    fontWeight: "bold",
                  }}>
                  COPY
                </button>
                <button
                  onClick={() => navigate(`/career`)}
                  style={{
                    padding: "8px 20px",
                    cursor: "pointer",
                    background: "rgba(255, 255, 255, 0.1)",
                    border: "1px solid rgba(255, 255, 255, 0.14)",
                    color: "white",
                    borderRadius: "90px",
                    fontWeight: "600",
                    fontSize: "16px",
                  }}>
                  BUILD MY TEAM
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="region-grid">
          {regions.map((r) => {
            const regionTrophyCount = trophies.filter((t) => t.region === r.code).length;
            const hasTrophy = regionTrophyCount > 0;

            // Every card already represents ONE competition → use r.code directly
            const competitionKey = r.code;

            // Find league using normalized key
            const activeLeague = activeLeagues.find((l) => (l.event_code ?? l.region) === competitionKey);

            // seasonStatus should also be keyed the same way
            const status = seasonStatus[competitionKey];
            const isLocked = status?.state === "locked";
            const isEnded = status?.state === "ended";
            // Determine click behavior
            const handleClick = () => {
              if (isEnded) {
                toast.error("Season has already ended for this region");
                return;
              }
              if (activeLeague) {
                navigate(`/dashboard/league/${activeLeague.id}`);
              } else {
                handleJoinRegion(r.code, r.maxPlayers);
              }
            };

            return (
              <div
                key={r.code}
                className="region-card"
                onClick={() => {
                  if (isLocked || isEnded) return;
                  handleClick();
                }}
                style={{
                  position: "relative",
                  background: `linear-gradient(200deg, rgba(${r.color2}, 1) 0%, rgba(${r.color}, 1) 80%)`,
                  border: activeLeague ? "2px solid #00ff90" : "none",
                  transition: "all 0.25s ease",
                  opacity: isLocked || isEnded ? 0.4 : 1,
                  cursor: isLocked || isEnded ? "not-allowed" : "pointer",
                }}
                onMouseEnter={(e) => {
                  if (!isLocked && !isEnded) e.currentTarget.style.opacity = "0.9";
                }}
                onMouseLeave={(e) => {
                  if (!isLocked && !isEnded) e.currentTarget.style.opacity = "1";
                }}>
                {/* Active Badge */}
                {activeLeague && (
                  <div
                    style={{
                      position: "absolute",
                      top: "6px",
                      left: "6px",
                      background: "#00ff90",
                      color: "#000",
                      fontSize: "11px",
                      fontWeight: "bold",
                      padding: "2px 6px",
                      borderRadius: "40px",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}>
                    Registered
                  </div>
                )}
                {isLocked && (
                  <div
                    style={{
                      position: "absolute",
                      display: "flex",
                      textAlign: "center",
                      alignItems: "center",
                      justifyContent: "center",
                      top: 0,
                      padding: "10px",
                      fontSize: "13px",
                      fontWeight: "600",
                      letterSpacing: "0.3px",
                      color: "white",
                    }}>
                    Registration Closed
                    <br />
                    After Week {status?.lock_week - 1}
                  </div>
                )}
                {isEnded && (
                  <div
                    style={{
                      position: "absolute",
                      top: 0,
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: "10px",
                      fontSize: "13px",
                      fontWeight: "600",
                      color: "white",
                      textAlign: "center",
                    }}>
                    Season Ended
                  </div>
                )}
                {!activeLeague && !isEnded && !isLocked && (
                  <div
                    style={{
                      position: "absolute",
                      top: "6px",
                      left: "6px",
                      background: "#00ff90",
                      color: "#000",
                      fontSize: "11px",
                      fontWeight: "bold",
                      padding: "2px 6px",
                      borderRadius: "40px",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}>
                    LEAGUE READY
                  </div>
                )}
                <img src={r.banner} alt={r.name} style={{ height: "46px" }} />
                <h2 className="region-name">{r.name}</h2>
                <img
                  src={hasTrophy ? r.trophyUnlocked : r.trophyLocked}
                  alt={r.name}
                  style={{
                    height: "46px",
                    opacity: hasTrophy ? "1" : "0.4",
                    filter: "drop-shadow(0 1px 0px rgba(155, 155, 155, 0.4))",
                  }}
                />
                <p style={{ color: "white", opacity: 0.5, fontSize: "12px", marginTop: "20px", margin: "10px", textAlign: "center" }}>
                  {!hasTrophy ? "Win this region to claim the trophy" : regionTrophyCount}
                </p>
              </div>
            );
          })}

          {internationalEvents.map((event) => {
            const competitionKey = event.code;

            const activeLeague = activeLeagues.find((l) => (l.event_code ?? l.region) === competitionKey);
            const status = seasonStatus[competitionKey];
            const isLockedBySeason = status?.state === "locked";

            const start = new Date(event.startDate);
            const hasTicket = tickets > 0;
            const isUpcoming = now < start;

            // Lock if user can't enter AND isn't already registered
            const locked = !activeLeague && (isLockedBySeason || !hasTicket || isUpcoming);
            const internationalTrophyCount = trophies.filter((t) => t.region === competitionKey).length;
            const hasTrophy = internationalTrophyCount > 0;

            const handleClick = () => {
              if (activeLeague) {
                navigate(`/dashboard/league/${activeLeague.id}`);
              } else {
                handleJoinEvent(event.code, event.maxPlayers);
              }
            };

            return (
              <div className="shimmer-wrapper" style={{ borderRadius: "0" }} key={event.code}>
                <div
                  className="region-card"
                  onClick={() => {
                    if (locked) return;
                    handleClick();
                  }}
                  style={{
                    background: `linear-gradient(200deg, rgba(${event.color2}, 1) 0%, rgba(${event.color}, 1) 80%)`,
                    opacity: locked ? "0.4" : "1",
                    border: activeLeague ? "2px solid #00ff90" : "2px solid transparent",
                    cursor: locked ? "not-allowed" : "pointer",
                  }}>
                  {activeLeague && (
                    <div
                      style={{
                        position: "absolute",
                        top: "6px",
                        left: "6px",
                        background: "#00ff90",
                        color: "#000",
                        fontSize: "11px",
                        fontWeight: "bold",
                        padding: "2px 6px",
                        borderRadius: "40px",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                      }}>
                      Registered
                    </div>
                  )}

                  <img src={event.banner} alt={event.name} style={{ height: "46px", opacity: locked ? 0.4 : 1 }} />
                  <h2 className="region-name">{event.name}</h2>

                  <img
                    src={hasTrophy ? event.trophyUnlocked : event.trophyLocked}
                    alt={event.name}
                    style={{
                      height: "46px",
                      opacity: hasTrophy ? "1" : "0.4",
                      filter: "drop-shadow(0 1px 0px rgba(155,155,155,0.4))",
                    }}
                  />

                  {!activeLeague && locked && (
                    <div style={{ color: "white", opacity: 0.7, fontSize: "12px", marginTop: "20px", textAlign: "center" }}>
                      Earn an International Ticket to unlock
                    </div>
                  )}

                  <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                    <img src="/international-ticket.png" style={{ height: "28px" }} alt="International Tickets" />
                    <p style={{ color: "white" }}>1</p>
                  </div>
                </div>
              </div>
            );
          })}
          <div
            className="region-card"
            onClick={() => setShowCreate(true)}
            style={{
              position: "relative",
              background: "#2A6FFF",
              cursor: "pointer",
              color: "white",
              transition: "all 0.25s ease",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.9")}
            onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}>
            <span style={{ fontSize: "42px" }}>&#43;</span>
            <h4>Create Friends League</h4>
          </div>
          <div
            className="region-card"
            onClick={() => setShowJoin(true)}
            style={{
              position: "relative",
              background: "rgba(255, 255, 255, 0.1)",
              color: "white",
              cursor: "pointer",
              transition: "all 0.25s ease",
              border: "1px solid rgba(255, 255, 255, 0.1)",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.9")}
            onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}>
            <Barcode />
            <h4 style={{ marginTop: "20px" }}>Join with Friend Code</h4>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
